import React, { useState, useRef, useEffect } from 'react';
import coachProfileService from '../../../services/coachProfileService';

export const StatsFilterModal = ({ isOpen, onClose, onApply, buttonRef, profileService, defaultSelectedFilters = [], defaultActiveTab = 'hitting' }) => {
  // Use passed profileService or default to coachProfileService
  const service = profileService || coachProfileService;
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filterTabMap, setFilterTabMap] = useState({}); // { filterId: 'hitting' | 'pitching' }
  const [filterRanges, setFilterRanges] = useState({}); // { filterId: { min: '', max: '' } }
  const [savedFilters, setSavedFilters] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const modalRef = useRef(null);
  const prevDefaultKeyRef = useRef(null);

  // Load saved filters on mount + initialize defaults
  useEffect(() => {
    if (isOpen) {
      loadSavedFilters();
      const currentKey = defaultSelectedFilters.join(',');
      const defaultsChanged = prevDefaultKeyRef.current !== currentKey;
      if (selectedFilters.length === 0 || defaultsChanged) {
        setSelectedFilters(defaultSelectedFilters);
        setActiveTab(defaultActiveTab);
        const tabMap = {};
        defaultSelectedFilters.forEach(id => { tabMap[id] = defaultActiveTab; });
        setFilterTabMap(tabMap);
      }
      prevDefaultKeyRef.current = currentKey;
    }
  }, [isOpen]);

  const loadSavedFilters = async () => {
    setIsLoadingFilters(true);
    try {
      const response = await service.getFilters();
      setSavedFilters(response.data || []);
    } catch (error) {
      console.error('Failed to load saved filters:', error);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) &&
          buttonRef?.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, buttonRef]);

  const hittingFilters = [
    { id: 'avg', label: 'AVG', fullName: 'Batting Average' },
    { id: 'obp', label: 'OBP', fullName: 'On Base Percentage' },
    { id: 'slg', label: 'SLG', fullName: 'Slugging Percentage' },
    { id: 'ops', label: 'OPS', fullName: 'On Base Plus Slugging' },
    { id: 'iso', label: 'ISO', fullName: 'Isolated Power' },
    { id: 'woba', label: 'wOBA', fullName: 'Weighted On Base Average' },
    { id: 'wrc+', label: 'wRC+', fullName: 'Weighted Runs Created Plus' },
    { id: 'ops+', label: 'OPS+', fullName: 'OPS Plus' },
    { id: 'k%', label: 'K%', fullName: 'Strikeout Percentage' },
    { id: 'bb%', label: 'BB%', fullName: 'Walk Percentage' },
    { id: 'k', label: 'K', fullName: 'Strikeouts' },
    { id: 'bb', label: 'BB', fullName: 'Walks' },
    { id: 'k/bb', label: 'K/BB', fullName: 'Strikeout to Walk Ratio' },
    { id: 'hr', label: 'HR', fullName: 'Home Runs' },
    { id: 'h', label: 'H', fullName: 'Hits' },
    { id: '2b', label: '2B', fullName: 'Doubles' },
    { id: '3b', label: '3B', fullName: 'Triples' },
    { id: 'rbi', label: 'RBI', fullName: 'Runs Batted In' },
    { id: 'runs', label: 'RUNS', fullName: 'Runs' },
    { id: 'ab', label: 'AB', fullName: 'At Bats' },
    { id: 'pa', label: 'PA', fullName: 'Plate Appearances' },
    { id: 'babip', label: 'BABIP', fullName: 'Batting Average on Balls In Play' },
  ];

  const pitchingFilters = [
    { id: 'era', label: 'ERA', fullName: 'Earned Run Average' },
    { id: 'whip', label: 'WHIP', fullName: 'Walks + Hits Per IP' },
    { id: 'fip', label: 'FIP', fullName: 'Fielding Independent Pitching' },
    { id: 'k%', label: 'K%', fullName: 'Strikeout Percentage' },
    { id: 'bb%', label: 'BB%', fullName: 'Walk Percentage' },
    { id: 'ip', label: 'IP', fullName: 'Innings Pitched' },
    { id: 'gs', label: 'GS', fullName: 'Games Started' },
    { id: 'app', label: 'APP', fullName: 'Appearances' },
    { id: 'so', label: 'SO', fullName: 'Strikeouts' },
    { id: 'bb', label: 'BB', fullName: 'Walks' },
    { id: 'k-bb%', label: 'K-BB%', fullName: 'Strikeout Minus Walk Percentage' },
    { id: 'k/bb', label: 'K/BB', fullName: 'Strikeout to Walk Ratio' },
    { id: 'fip+', label: 'FIP+', fullName: 'FIP Plus' },
    { id: 'era+', label: 'ERA+', fullName: 'ERA Plus' },
    { id: 'xfip', label: 'xFIP', fullName: 'Expected FIP' },
    { id: 'lob%', label: 'LOB%', fullName: 'Left On Base Percentage' },
    { id: 'babip-against', label: 'BABIP Against', fullName: 'BABIP Against' },
    { id: 'baa', label: 'BAA', fullName: 'Batting Average Against' },
    { id: 'hbp', label: 'HBP', fullName: 'Hit Batters' },
  ];

  const getCurrentFilters = () => {
    switch(activeTab) {
      case 'hitting': return hittingFilters;
      case 'pitching': return pitchingFilters;
      default: return hittingFilters;
    }
  };

  const getAllFilters = () => [...hittingFilters, ...pitchingFilters];

  const isFilterSelected = (filterId) =>
    selectedFilters.includes(filterId) && filterTabMap[filterId] === activeTab;

  const toggleFilter = (filterId) => {
    const alreadySelected = isFilterSelected(filterId);
    if (alreadySelected) {
      setSelectedFilters(prev => prev.filter(id => id !== filterId));
      setFilterRanges(prev => { const n = { ...prev }; delete n[filterId]; return n; });
      setFilterTabMap(prev => { const n = { ...prev }; delete n[filterId]; return n; });
    } else {
      if (!selectedFilters.includes(filterId)) {
        setFilterRanges(prev => ({ ...prev, [filterId]: { min: '', max: '' } }));
        setSelectedFilters(prev => [...prev, filterId]);
      }
      setFilterTabMap(prev => ({ ...prev, [filterId]: activeTab }));
    }
  };

  const handleRangeChange = (filterId, field, value) => {
    let sanitized = value;
    // Percentages must be whole numbers between 1 and 100 (no decimals)
    if (percentageStats.includes(filterId) && value !== '') {
      const cleaned = value.split('.')[0].replace(/[^\d]/g, '');
      if (cleaned === '') {
        sanitized = '';
      } else {
        let intVal = parseInt(cleaned, 10);
        if (intVal > 100) intVal = 100;
        if (intVal < 1) intVal = 1;
        sanitized = String(intVal);
      }
    }
    setFilterRanges(prev => ({
      ...prev,
      [filterId]: {
        ...prev[filterId],
        [field]: sanitized
      }
    }));
  };

  const removeStatRange = (filterId) => {
    setSelectedFilters(prev => prev.filter(id => id !== filterId));
    setFilterRanges(prev => { const n = { ...prev }; delete n[filterId]; return n; });
    setFilterTabMap(prev => { const n = { ...prev }; delete n[filterId]; return n; });
  };

  const getFilterInfo = (filterId) => {
    return getAllFilters().find(f => f.id === filterId) || { id: filterId, label: filterId.toUpperCase(), fullName: filterId };
  };

  // Stats that use decimal format (0.xxx)
  const decimalStats = ['avg', 'obp', 'slg', 'ops', 'iso', 'baa', 'woba', 'babip', 'babip-against'];
  // Stats that use percentage format
  const percentageStats = ['k%', 'bb%', 'k-bb%', 'lob%'];

  const getPlaceholder = (filterId, type) => {
    if (decimalStats.includes(filterId)) {
      return type === 'min' ? '.200' : '.500';
    }
    if (percentageStats.includes(filterId)) {
      return type === 'min' ? '5' : '30';
    }
    if (['era', 'whip'].includes(filterId)) {
      return type === 'min' ? '1.00' : '5.00';
    }
    return type === 'min' ? '0' : '100';
  };

  const getStep = (filterId) => {
    if (decimalStats.includes(filterId)) return '0.001';
    if (['era', 'whip'].includes(filterId)) return '0.01';
    if (percentageStats.includes(filterId)) return '1';
    return 'any';
  };

  // Min/Max bounds for the number inputs (percentages are restricted to 1-100)
  const getInputBounds = (filterId) =>
    percentageStats.includes(filterId) ? { min: 1, max: 100 } : {};

  // Check if any filter has min > max validation error
  const getValidationErrors = () => {
    const errors = {};
    selectedFilters.forEach(filterId => {
      const range = filterRanges[filterId] || { min: '', max: '' };
      if (range.min !== '' && range.max !== '' && parseFloat(range.min) > parseFloat(range.max)) {
        errors[filterId] = 'Min cannot be greater than Max';
      }
    });
    return errors;
  };

  const validationErrors = getValidationErrors();
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const handleApply = () => {
    if (hasValidationErrors) return;

    // Build filter data with ranges for API
    const filtersWithRanges = selectedFilters.map(filterId => ({
      id: filterId,
      ...filterRanges[filterId]
    }));

    onApply({
      selectedFilters,
      filterRanges,
      filtersWithRanges,
      activeTab
    });
    onClose();
  };

  const handleClearFilters = () => {
    setSelectedFilters([]);
    setFilterRanges({});
    setFilterTabMap({});
    onApply({
      selectedFilters: [],
      filterRanges: {},
      filtersWithRanges: [],
      activeTab
    });
    onClose();
  };

  const handleSaveFilter = async () => {
    if (!newFilterName.trim()) return;

    setIsSaving(true);
    try {
      // Separate filters by category
      const hittingIds = hittingFilters.map(f => f.id);
      const pitchingIds = pitchingFilters.map(f => f.id);

      const selectedHitting = selectedFilters.filter(id => hittingIds.includes(id));
      const selectedPitching = selectedFilters.filter(id => pitchingIds.includes(id));

      // Build stats array with { stat, minValue, maxValue } format
      const buildStatsArray = (filterIds) => {
        return filterIds.map(id => {
          const filterInfo = getFilterInfo(id);
          const range = filterRanges[id] || { min: '', max: '' };
          return {
            stat: filterInfo.label,
            minValue: range.min ? parseFloat(range.min) : 0,
            maxValue: range.max ? parseFloat(range.max) : 0
          };
        });
      };

      const filterData = {
        name: newFilterName.trim(),
        hittingStats: buildStatsArray(selectedHitting),
        pitchingStats: buildStatsArray(selectedPitching),
      };

      await service.saveFilter(filterData);
      await loadSavedFilters();
      setNewFilterName('');
      setShowSaveInput(false);
    } catch (error) {
      console.error('Failed to save filter:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadSavedFilter = (filter) => {
    // Map stat label to UI filter ID (lowercase)
    const labelToId = {};
    getAllFilters().forEach(f => {
      labelToId[f.label.toUpperCase()] = f.id;
    });

    const uiFilters = [];
    const ranges = {};
    const tabMap = {};

    const processStats = (statsArray, tab) => {
      (statsArray || []).forEach(statObj => {
        const statLabel = statObj.stat?.toUpperCase();
        const filterId = labelToId[statLabel] || statObj.stat?.toLowerCase();
        if (filterId) {
          uiFilters.push(filterId);
          tabMap[filterId] = tab;
          ranges[filterId] = {
            min: statObj.minValue ? statObj.minValue.toString() : '',
            max: statObj.maxValue ? statObj.maxValue.toString() : ''
          };
        }
      });
    };

    processStats(filter.hittingStats, 'hitting');
    processStats(filter.pitchingStats, 'pitching');

    setSelectedFilters(uiFilters);
    setFilterRanges(ranges);
    setFilterTabMap(tabMap);
  };

  const handleDeleteFilter = async (filterId, e) => {
    e.stopPropagation();
    try {
      await service.deleteFilter(filterId);
      await loadSavedFilters();
    } catch (error) {
      console.error('Failed to delete filter:', error);
    }
  };

  if (!isOpen) return null;

  const currentFilters = getCurrentFilters();

  return (
    <div
      ref={modalRef}
      className="absolute right-0 top-full mt-2 bg-jp-card rounded-xl shadow-2xl border border-jp-line
                 w-full max-w-[950px] sm:w-[650px] md:w-[800px] lg:w-[950px]
                 max-h-[85vh] overflow-hidden z-50 flex flex-col"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-jp-line flex items-center justify-between bg-jp-section">
        <h2 className="text-jp-ink text-base font-bold">Add/Subtract Filters</h2>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-jp-line rounded-lg transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-jp-muted">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* Content - Two Column Layout */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
        {/* Left Sidebar - Saved Filters */}
        <div className="lg:w-[200px] border-b lg:border-b-0 lg:border-r border-jp-line p-4 lg:overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-jp-ink text-sm font-bold">SAVED FILTERS</h3>
            <button
              onClick={() => setShowSaveInput(!showSaveInput)}
              className="p-1.5 hover:bg-jp-line rounded-lg transition-colors text-jp-accent hover:text-jp-accent/80"
              title="Save current filter"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
              </svg>
            </button>
          </div>

          {/* Save Input */}
          {showSaveInput && (
            <div className="mb-3 space-y-2">
              <input
                type="text"
                value={newFilterName}
                onChange={(e) => setNewFilterName(e.target.value)}
                placeholder="Filter name..."
                className="w-full px-3 py-2 bg-jp-section border border-jp-line rounded-lg text-jp-ink text-xs outline-none focus:border-jp-accent"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveFilter}
                  disabled={!newFilterName.trim() || selectedFilters.length === 0 || isSaving}
                  className="flex-1 px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 rounded-lg text-slate-900 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setShowSaveInput(false);
                    setNewFilterName('');
                  }}
                  className="px-3 py-1.5 bg-jp-section hover:bg-jp-line rounded-lg text-jp-muted text-xs transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Saved Filters List */}
          {isLoadingFilters ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-5 h-5 border-2 border-jp-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : savedFilters.length === 0 ? (
            <p className="text-jp-muted/80 text-xs">No saved filters yet.</p>
          ) : (
            <div className="space-y-2 lg:max-h-[400px] overflow-y-auto">
              {savedFilters.map((filter) => (
                <div
                  key={filter._id}
                  onClick={() => handleLoadSavedFilter(filter)}
                  className="group flex items-center justify-between px-3 py-2 bg-jp-section hover:bg-jp-line border border-jp-line hover:border-jp-accent/60 rounded-lg cursor-pointer transition-all"
                >
                  <span className="text-jp-muted text-xs truncate flex-1">{filter.name}</span>
                  <button
                    onClick={(e) => handleDeleteFilter(filter._id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all ml-2 flex-shrink-0"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-600 dark:text-red-400">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Content - Tabs & Filters */}
        <div className="flex-1 flex flex-col overflow-y-auto p-4">
          {/* Tabs */}
          <div className="flex gap-1 mb-4 border-b border-jp-line flex-shrink-0">
            {['hitting', 'pitching'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  activeTab === tab
                    ? 'text-jp-ink border-b-2 border-jp-accent'
                    : 'text-jp-muted/80 hover:text-jp-ink'
                }`}
              >
                {tab} STATS
              </button>
            ))}
          </div>

          {/* Filter Checkboxes Grid */}
          <div className="mb-4 flex-shrink-0">
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 gap-2">
              {currentFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => toggleFilter(filter.id)}
                  className={`px-2 py-2 rounded-lg border transition-all text-xs font-medium flex items-center justify-center gap-1 ${
                    isFilterSelected(filter.id)
                      ? 'bg-jp-accent/10 border-jp-accent text-jp-accent'
                      : 'bg-jp-section border-jp-line text-jp-muted hover:border-jp-accent/60'
                  }`}
                  title={filter.fullName}
                >
                  {isFilterSelected(filter.id) && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-jp-accent flex-shrink-0">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                    </svg>
                  )}
                  <span className="truncate">{filter.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Stat Ranges Section - Horizontal Scroll */}
          {selectedFilters.length > 0 && (
            <div className="border-t border-jp-line pt-4 flex-1 min-h-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-jp-ink text-sm font-bold">STAT RANGES</h3>
                <span className="text-jp-muted/80 text-xs">{selectedFilters.length} selected</span>
              </div>
              {/* Horizontal scrollable container */}
              <div className="overflow-x-auto pb-2">
                <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
                  {selectedFilters.map((filterId) => {
                    const filterInfo = getFilterInfo(filterId);
                    const range = filterRanges[filterId] || { min: '', max: '' };

                    return (
                      <div
                        key={filterId}
                        className="bg-jp-section border border-jp-line rounded-lg p-3 flex-shrink-0"
                        style={{ width: '180px' }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-jp-accent text-xs font-bold">{filterInfo.label}</span>
                          <button
                            onClick={() => removeStatRange(filterId)}
                            className="p-1 hover:bg-red-500/20 rounded transition-colors"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-jp-muted hover:text-red-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                        <p className="text-jp-muted/80 text-[10px] mb-2 truncate">{filterInfo.fullName}</p>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-jp-muted/80 text-[10px] block mb-1">Min</label>
                            <input
                              type="number"
                              step={getStep(filterId)}
                              {...getInputBounds(filterId)}
                              value={range.min}
                              onChange={(e) => handleRangeChange(filterId, 'min', e.target.value)}
                              placeholder={getPlaceholder(filterId, 'min')}
                              className={`w-full px-2 py-1.5 bg-jp-section border rounded text-jp-ink text-xs outline-none ${
                                validationErrors[filterId] ? 'border-red-500 focus:border-red-500' : 'border-jp-line focus:border-jp-accent'
                              }`}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-jp-muted/80 text-[10px] block mb-1">Max</label>
                            <input
                              type="number"
                              step={getStep(filterId)}
                              {...getInputBounds(filterId)}
                              value={range.max}
                              onChange={(e) => handleRangeChange(filterId, 'max', e.target.value)}
                              placeholder={getPlaceholder(filterId, 'max')}
                              className={`w-full px-2 py-1.5 bg-jp-section border rounded text-jp-ink text-xs outline-none ${
                                validationErrors[filterId] ? 'border-red-500 focus:border-red-500' : 'border-jp-line focus:border-jp-accent'
                              }`}
                            />
                          </div>
                        </div>
                        {validationErrors[filterId] && (
                          <p className="text-red-600 dark:text-red-400 text-[10px] mt-1">{validationErrors[filterId]}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-jp-line flex items-center justify-between bg-jp-section flex-shrink-0">
        <span className="text-jp-muted/80 text-xs">
          {selectedFilters.length} filter{selectedFilters.length !== 1 ? 's' : ''} selected
        </span>
        <div className="flex gap-2">
          <button
            onClick={handleClearFilters}
            disabled={selectedFilters.length === 0}
            className="px-4 py-2 bg-jp-section hover:bg-jp-line border border-jp-line rounded-lg text-jp-ink text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset Filters
          </button>
          <button
            onClick={handleApply}
            disabled={hasValidationErrors}
            className={`px-6 py-2 rounded-lg text-slate-900 text-sm font-bold transition-colors ${
              hasValidationErrors
                ? 'bg-cyan-800 cursor-not-allowed opacity-50'
                : 'bg-cyan-500 hover:bg-cyan-400'
            }`}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};
